import { StageContainer } from '../../appeals/list/columns'
import dayjs from 'dayjs'
import { Value } from '../../elements'
import { Badge, Modal, Tag } from 'antd'
import React, { useState } from 'react'
import { IDeleteRecord, ISorterProps } from './interfaces'
import { ButtonActionWrapper } from '../../../components/list-operations/styles'
import { DeleteTwoTone } from '@ant-design/icons'

const formatPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return <Value data={undefined} />
  }
  const cleaned = ('' + phoneNumber).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/)
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}-${match[5]}`
  }
  return phoneNumber
}

export const getColumns = (
  sorter: ISorterProps,
  leader: boolean,
  deleteRecord: (id: number, removeClaim: boolean) => Promise<IDeleteRecord>,
) => {
  const columnsArr = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    {
      title: 'Стадия',
      dataIndex: 'stage',
      key: 'stage',
      sorter: true,
      width: 150,
      render: stage => <StageContainer stage={stage} />,
    },
    {
      title: 'Ответственный за работу',
      dataIndex: 'responsible_name',
      key: 'responsible_name',
      width: 220,
      render: val => (val && Array.isArray(val) ? val.join(', ') : val),
    },
    {
      title: 'Дата исполнения работ (план)',
      dataIndex: 'date_work_completion',
      key: 'date_work_completion',
      width: 130,
      render: val => dayjs(val).format('DD.MM.YYYY'),
    },
    {
      title: 'Время исполнения работ (план)',
      dataIndex: 'time_work_completion',
      key: 'time_work_completion',
      width: 130,
      render: (values?: string[]) => {
        if (values?.length) {
          return (
            <>
              {values.map((time, k) => (
                <Tag key={k}>{time}</Tag>
              ))}
            </>
          )
        }
        return <Value data={values} />
      },
    },
    {
      title: 'Дата и время исполнения работ (факт)',
      dataIndex: 'datetime_work_completion',
      key: 'datetime_work_completion',
      width: 130,
      render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
    },
    {
      title: 'Адрес объекта',
      dataIndex: 'address',
      key: 'address',
      width: 200,
    },
    {
      title: 'Суть претензии',
      dataIndex: 'claim',
      key: 'claim',
      width: 300,
    },
    {
      title: 'Номер переделки',
      dataIndex: 'num_rework',
      key: 'num_rework',
      width: 130,
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      render: formatPhoneNumber,
      width: 150,
    },
    {
      title: 'Ответственный дилер',
      dataIndex: 'dealer_name',
      key: 'dealer_name',
      width: 140,
      render: val => <Value data={val} />,
    },
    {
      title: 'Классификация',
      dataIndex: 'classification',
      key: 'classification',
      width: 150,
    },
    {
      title: 'Количество изделий',
      dataIndex: 'product_count',
      key: 'product_count',
      width: 150,
    },
    {
      title: 'Дата создания',
      width: 170,
      dataIndex: 'created_at',
      key: 'created_at',
      render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
    },
    {
      title: 'Дата обновления',
      width: 170,
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
    },
  ] as any[]

  const columnAction = {
    title: 'Действие',
    key: 'operation',
    dataIndex: 'operation',
    className: 'no-edit',
    fixed: 'right',
    width: 100,
    align: 'center',
    render: (_, { id }) => {
      return (
        <span
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <DeleteButton id={id} deleteRecord={deleteRecord} />
        </span>
      )
    },
  }

  const DeleteButton = ({ id, deleteRecord }) => {
    const [deleteState, setDeleteState] = useState<'idle' | 'confirming' | 'confirmingWithClaim'>(
      'idle',
    )
    const [isDeleting, setIsDeleting] = useState(false)

    const modalConfig = {
      confirming: {
        title: 'Подтверждение удаления',
        content: `Вы уверены, что хотите удалить заявку № ${id}?`,
        okText: 'Да',
      },
      confirmingWithClaim: {
        title: 'Внимание!',
        content: `На основании этой заявки уже создана рекламация. Удалить заявку и рекламацию № ${id}?`,
        okText: 'Да, удалить заявку и рекламацию!',
      },
    }

    const { title, content, okText } = modalConfig[deleteState] || {}

    const handleInitialDelete = () => setDeleteState('confirming')

    const handleConfirmDelete = async () => {
      setIsDeleting(true)
      try {
        const result = await deleteRecord(id)
        if (result?.hasClaim) setDeleteState('confirmingWithClaim')
        else setDeleteState('idle')
      } finally {
        setIsDeleting(false)
      }
    }

    const handleForceDelete = async () => {
      setIsDeleting(true)
      try {
        await deleteRecord(id, true)
        setDeleteState('idle')
      } finally {
        setIsDeleting(false)
      }
    }

    const handleCancel = () => setDeleteState('idle')

    const okHandler = deleteState === 'confirming' ? handleConfirmDelete : handleForceDelete

    return (
      <>
        <ButtonActionWrapper title="Удалить" onClick={handleInitialDelete} disabled={isDeleting}>
          <DeleteTwoTone />
        </ButtonActionWrapper>

        <Modal
          title={title}
          open={deleteState !== 'idle'}
          onOk={okHandler}
          onCancel={handleCancel}
          okText={okText}
          cancelText="Нет"
          confirmLoading={isDeleting}
          closable={!isDeleting}
          maskClosable={!isDeleting}
          keyboard={!isDeleting}
          okButtonProps={{
            loading: isDeleting,
            type: 'primary',
            danger: true,
          }}
          cancelButtonProps={{ disabled: isDeleting }}
        >
          {content}
        </Modal>
      </>
    )
  }

  if (leader) {
    columnsArr.push(columnAction)
  }
  return columnsArr.map(item => {
    if (item.key === sorter.sort_field) {
      item.sortOrder = sorter.sort_order
    } else {
      delete item.sortOrder
    }
    return item
  })
}
